import {
  type AcquireBookErrorType,
  type AcquireBookRealEstateType,
} from "../types";
import { type AcquireBookErrorBadge } from "./errorClassifier";

export interface AcquireBookStatusCellMeta {
  badge: AcquireBookErrorBadge | null;
  detail: string | null;
}

export interface AcquireBookStatusDataGridRow {
  id: number;
  /**
   * この行が指す物件の識別子。Salesforce連携の送信単位であり、明細の主キーではない。
   * 経過日数を指定した依頼では種別ごとに中古登記が混ざり、同じ物件でも明細ごとに
   * unique_id が分かれるため配列で持つ。
   */
  uniqueIds: string[];
  /**
   * 行全体としての他ユーザー取得フラグ。
   * mergeRowsByProperty で AND 集約されるため、同一物件の明細が 1 件でも
   * 自ユーザー取得なら false になる。連携対象の picture_request_detail を
   * 抽出する用途では使えない（フィルタには下記の per-detail フラグを使うこと）。
   */
  isOtherUserResource: boolean;
  /** DataLens 一括依頼のときのみ表示に使う（通常依頼は空文字） */
  companyName: string;
  realEstateType: AcquireBookRealEstateType;
  prefectureName: string;
  location: string;
  normalizedLocation: string;
  chibanKaokuNumber: string;
  normalizedChiban: string;
  normalizedFreeWord: string;
  pictureReceivedDate: Date | null;
  allMattersInfo: string;
  allMattersDetailId: number;
  /**
   * allMattersDetailId が他ユーザー所有かどうか。
   * 同一物件で自分が allMatters、他ユーザーが owner を取得しているケースで、
   * 行全体の isOtherUserResource が false でも個別明細レベルでは別物件として
   * 扱う必要があるため per-detail で保持する。
   */
  allMattersIsOtherUser: boolean;
  allMattersInfoMeta: AcquireBookStatusCellMeta | null;
  allMattersInfoDate: Date | null;
  ownerInfo: string;
  ownerInfoDetailId: number;
  /** ownerInfoDetailId が他ユーザー所有かどうか。@see allMattersIsOtherUser */
  ownerInfoIsOtherUser: boolean;
  ownerInfoMeta: AcquireBookStatusCellMeta | null;
  ownerInfoDate: Date | null;
  electricDrawings: string;
  electricDrawingsMeta: AcquireBookStatusCellMeta | null;
  electricDrawingsDate: Date | null;
  tisekiDrawings: string;
  tisekiDrawingsMeta: AcquireBookStatusCellMeta | null;
  tisekiDrawingsDate: Date | null;
  tiekiDrawings: string;
  tiekiDrawingsMeta: AcquireBookStatusCellMeta | null;
  tiekiDrawingsDate: Date | null;
  buildingDrawings: string;
  buildingDrawingsMeta: AcquireBookStatusCellMeta | null;
  buildingDrawingsDate: Date | null;
  errorTypes: AcquireBookErrorType[];
  status: string;
}

export interface RowAggregationOptions {
  /**
   * Salesforce連携の識別子を集約するか。連携を使わない利用者には不要な処理のため、
   * 画面側の判定結果を渡して切り替える。この関数から機能フラグを直接参照しない。
   */
  collectsUniqueIds?: boolean;
}

// 同じ都道府県、住所、地番を持っている項目は一つにマージする
export const mergeRowsByProperty = (
  rows: AcquireBookStatusDataGridRow[],
  options: RowAggregationOptions = {}
): AcquireBookStatusDataGridRow[] => {
  const { collectsUniqueIds = false } = options;

  const pickLatestDate = (
    current: Date | null,
    incoming: Date | null
  ): Date | null => {
    if (incoming === null) return current;
    if (current === null) return incoming;
    return current < incoming ? incoming : current;
  };

  const collectErrorTypes = (
    row: AcquireBookStatusDataGridRow
  ): AcquireBookErrorType[] => {
    const metas: Array<AcquireBookStatusCellMeta | null> = [
      row.ownerInfoMeta,
      row.tisekiDrawingsMeta,
      row.electricDrawingsMeta,
      row.tiekiDrawingsMeta,
      row.buildingDrawingsMeta,
      row.allMattersInfoMeta,
    ];
    return metas.reduce<AcquireBookErrorType[]>((acc, meta) => {
      const type = meta?.badge?.type;
      if (type) acc.push(type);
      return acc;
    }, []);
  };

  // 出力は明細の初出順にそろえる
  const byKey = new Map<string, AcquireBookStatusDataGridRow>();

  rows.forEach((value) => {
    const key = `${value.companyName}${value.realEstateType}${value.prefectureName}${value.location}${value.chibanKaokuNumber}`;
    const samePropertyItem = byKey.get(key);

    if (samePropertyItem === undefined) {
      byKey.set(key, value);
      return;
    }

    // 中古登記が混ざると同じ物件でも識別子が分かれるため、行にすべて束ねる
    if (collectsUniqueIds) {
      value.uniqueIds.forEach((uniqueId) => {
        if (!samePropertyItem.uniqueIds.includes(uniqueId)) {
          samePropertyItem.uniqueIds.push(uniqueId);
        }
      });
    }

    // 自分で取得した登記が1件でもあれば、マージ後も自分のリソースとして扱う
    samePropertyItem.isOtherUserResource =
      samePropertyItem.isOtherUserResource && value.isOtherUserResource;

    if (value.ownerInfo !== "") {
      samePropertyItem.ownerInfo = value.ownerInfo;
      samePropertyItem.ownerInfoDetailId = value.ownerInfoDetailId;
      // detail を上書きするとき、その detail の所有権フラグも追従させる
      samePropertyItem.ownerInfoIsOtherUser = value.ownerInfoIsOtherUser;
      samePropertyItem.ownerInfoMeta = value.ownerInfoMeta;
      samePropertyItem.ownerInfoDate = pickLatestDate(
        samePropertyItem.ownerInfoDate,
        value.ownerInfoDate
      );
    }

    if (value.tisekiDrawings !== "") {
      samePropertyItem.tisekiDrawings = value.tisekiDrawings;
      samePropertyItem.tisekiDrawingsMeta = value.tisekiDrawingsMeta;
      samePropertyItem.tisekiDrawingsDate = pickLatestDate(
        samePropertyItem.tisekiDrawingsDate,
        value.tisekiDrawingsDate
      );
    }

    if (value.electricDrawings !== "") {
      samePropertyItem.electricDrawings = value.electricDrawings;
      samePropertyItem.electricDrawingsMeta = value.electricDrawingsMeta;
      samePropertyItem.electricDrawingsDate = pickLatestDate(
        samePropertyItem.electricDrawingsDate,
        value.electricDrawingsDate
      );
    }

    if (value.tiekiDrawings !== "") {
      samePropertyItem.tiekiDrawings = value.tiekiDrawings;
      samePropertyItem.tiekiDrawingsMeta = value.tiekiDrawingsMeta;
      samePropertyItem.tiekiDrawingsDate = pickLatestDate(
        samePropertyItem.tiekiDrawingsDate,
        value.tiekiDrawingsDate
      );
    }

    if (value.buildingDrawings !== "") {
      samePropertyItem.buildingDrawings = value.buildingDrawings;
      samePropertyItem.buildingDrawingsMeta = value.buildingDrawingsMeta;
      samePropertyItem.buildingDrawingsDate = pickLatestDate(
        samePropertyItem.buildingDrawingsDate,
        value.buildingDrawingsDate
      );
    }

    if (value.allMattersInfo !== "") {
      samePropertyItem.allMattersInfo = value.allMattersInfo;
      samePropertyItem.allMattersDetailId = value.allMattersDetailId;
      samePropertyItem.allMattersIsOtherUser = value.allMattersIsOtherUser;
      samePropertyItem.allMattersInfoMeta = value.allMattersInfoMeta;
      samePropertyItem.allMattersInfoDate = pickLatestDate(
        samePropertyItem.allMattersInfoDate,
        value.allMattersInfoDate
      );
    }

    if (value.status === "SUCCEEDED") {
      // NOTE: もし1件でもstatusがSUCCEEDEDなら、valueのstatusを上書きして再取得可能にする
      samePropertyItem.status = value.status;
    }

    if (value.pictureReceivedDate !== null) {
      const current = samePropertyItem.pictureReceivedDate;
      if (current === null || current < value.pictureReceivedDate) {
        samePropertyItem.pictureReceivedDate = value.pictureReceivedDate;
      }
    }
  });

  return [...byKey.values()].map((value): AcquireBookStatusDataGridRow => {
    return {
      ...value,
      errorTypes: collectErrorTypes(value),
    };
  });
};
